import { Field, FormikHelpers, FormikProps } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import * as Yup from "yup";
import FormProvider from "../../components/forms/FormProvider";
import Input from "../../components/forms/Input";
import { useAuthentication } from "../../context/authentication";
import { useContacts } from "../../context/contacts";
import { useLocations } from "../../context/locations";
import { useWallets } from "../../context/wallets";
import { useWorkspaces } from "../../context/workspaces";
import AppShell from "../../layout/AppShell";
import { ApiResponse, fetchApi } from "../../utils/api";
import { MessageBox } from "./UpdateEmail";

const VerifyEmailCode: React.FC<RouteComponentProps> = (props) => {
  const { checkIsUserLoggedIn } = useAuthentication();
  const { loadWorkspaces } = useWorkspaces();
  const { fetchWallets } = useWallets();
  const { fetchLocations } = useLocations();
  const { fetchContacts } = useContacts();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryString = useMemo(
    () => new URLSearchParams(props.location.search),
    [props.location.search]
  );
  const history = useHistory();

  useEffect(() => {
    if (!queryString.has("email") || queryString.get("email") === "") {
      props.history.push("/");
    }
  }, [props.history, queryString]);

  const email = useMemo(
    () => (queryString.has("email") ? queryString.get("email") : ""),
    [queryString]
  );

  const code = useMemo(
    () => (queryString.has("code") ? queryString.get("code") : ""),
    [queryString]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const response = await fetchApi(`/api/auth/verify-email/code`, {
        method: "POST",
        body: JSON.stringify({ code: formValues.code, email })
      });

      const apiResponse: ApiResponse<{ valid: boolean }> =
        await response.json();

      if (apiResponse.status && apiResponse.data.valid) {
        await checkIsUserLoggedIn(true);

        await loadWorkspaces();

        fetchWallets();
        fetchContacts();
        fetchLocations();

        formikHelpers.setSubmitting(false);

        history.push("/workspace");

        return;
      }

      formikHelpers.setSubmitting(false);

      setErrorMessage(apiResponse?.message || "Something went wrong");
    },
    [
      checkIsUserLoggedIn,
      email,
      fetchContacts,
      fetchLocations,
      fetchWallets,
      history,
      loadWorkspaces
    ]
  );

  return (
    <AppShell
      {...props}
      currentPage="Validate email"
      className="px-3 sm:px-0"
      isLoading={false}
    >
      <div
        className="flex flex-col items-center justify-center dark:bg-gray-900 dark:text-white"
        style={{
          minHeight: "calc(100vh - 49px)",
          maxHeight: "calc(100vh - 49px)"
        }}
      >
        <MessageBox
          title={
            <span className="font-normal text-hotspotty-400 dark:text-white">
              Please enter the verification code that we sent to <b>{email}</b>{" "}
              to continue.
            </span>
          }
          message={
            <div className="w-full mt-4">
              <FormProvider
                error={errorMessage}
                submitButtonText="Continue"
                submittingSubmitButtonText="Sending..."
                cancelButtonText="Change email"
                onCancel={() => props.history.push("/auth/email/update")}
                classnames={{
                  form: "bg-gray-50 dark:bg-gray-800",
                  buttonWrapper: "pb-0 pr-0"
                }}
                formikProps={{
                  onSubmit,
                  initialValues: { code },
                  validationSchema: Yup.object().shape({
                    code: Yup.string().min(6).max(6).required("Required")
                  })
                }}
                orderedFieldNames={ORDERED_FIELD_NAMES}
              >
                {({ isSubmitting }: FormikProps<FormValues>) => {
                  return (
                    <Field
                      id="search_field"
                      name="code"
                      label="Code"
                      placeholder="Insert your code"
                      type="text"
                      classnames={{ wrapper: "w-full" }}
                      component={Input}
                      disabled={isSubmitting}
                    />
                  );
                }}
              </FormProvider>
            </div>
          }
        />
      </div>
    </AppShell>
  );
};

export default VerifyEmailCode;

//
// Utils
//

interface FormValues {
  code: string;
}

const ORDERED_FIELD_NAMES = ["code"];
